import { I18nLocale } from 'utils';

import { BaseProduct } from '@/shared/types/base-product';

import { ShippingDeliverySettings } from '../productList';

export type ProductRedirectResponse = {
  redirectTo: string | null;
};

export type ProductResponse = Product;

export interface VariantResponse {
  productId: string;
  options: VariantOption[];
}

export enum PriceType {
  Regular = 'regular',
  Sellout = 'sellout',
  Promotion = 'promotion',
}

export interface Product extends BaseProduct {
  attributeGroupId: string;
  attributes: Attribute[];
  attributesCollection: AttributeCollection[];
  brandId: string;
  brandImage: string;
  brandName: string;
  brandSlug: string;
  brandIsHiddenOnProductPage: boolean;
  breadcrumbs: Breadcrumb[];
  categoriesId: string[];
  categoryHomeId: string;
  categoryHomeName: string;
  categoryHomePath: CategoryHomePath[];
  createdAt: string;
  ean: string;
  defaultCalendar: Record<string, boolean>;
  localeCalendar: Record<string, boolean>;
  isActive: boolean;
  label: string;
  locale: I18nLocale;
  manufacturerSymbol: string | null;
  model: string | null;
  productId: string;
  slug: string;
  slugTemporary: string;
  type: 'simple' | 'collection';
  images: string[];
  variantGroupId?: string;
  currency: string;
  price: number;
  priceType: PriceType;
  promotionDateIsShowed: boolean;
  stock: number;
  packageType: string;
  series: string | null;
  shippingDeliverySettings: ShippingDeliverySettings;
  shippingTime: number;
  archivalPrice: number | null;
  promotionEndDate: string | null;
  previousPrice: number | null;
  importFlag: boolean;
}

export interface IProductComplementGroup {
  productComplementGroupId: string;
  name: string;
  position: number;
  products: Array<{
    name: string;
    position: number;
    data: IAncillaryProduct;
  }>;
}

export type AncillaryProducts = {
  groups: Array<IProductComplementGroup>;
  products: Array<IAncillaryProduct>;
};

export type AncillaryProductsResponse = {
  groups: Array<IProductComplementGroup>;
  products: Array<IAncillaryProduct>;
};

export interface Attribute {
  inSpecification: boolean;
  isFilterable: boolean;
  isRange: boolean;
  name: string;
  slug: string;
  values: string[];
  unit: string;
}

export interface VariantOption {
  label: string;
  availableVariantItems: VariantProduct[];
  otherVariantItems: VariantProduct[];
}

export interface AttributeCollection {
  name: string;
  ean: string;
  model: string;
  series?: string;
  manufacturerSymbol: string | null;
  attributes: Attribute[];
}

export interface SeriesProduct extends BaseProduct {
  productId: string;
  slug: string;
  images: string[];
  previousPrice: number;
  stock: number;
  archivalPrice: number;
  shippingTime: number;
  priceType: string;
  currency: string;
  hasPreviousPrice: true;
  brandName: string;
  categoryHomePath: CategoryHomePath[];
  price: number;
  importFlag: boolean;
}

export interface IAncillaryProduct extends BaseProduct {
  productId: string;
  images: string[];
  slug: string;
  price: number;
  stock: number;
  ean: string;
  brandName: string;
  categoryHomeId: string;
  categoryHomePath: CategoryHomePath[];
  promotionEndDate: string | null;
  regularPrice: number;
  importFlag: boolean;
  label: string;
  position: number;
}

export interface VariantProduct {
  label: string;
  productId: string;
  productData: VariantProductData;
}

interface VariantProductData {
  images: string[];
  name: string;
  slug: string;
  currency: string;
  price: number;
  stock: number;
  oldPrice: number;
}

export interface Breadcrumb {
  name: string;
  slug: string | null;
}

export interface CategoryHomePath {
  name: string;
  slug: string;
}

export interface CrossRewriteProduct {
  product_id: string;
  locale: string;
  label: string;
  url: string;
}

export type CrossRewriteProductResponse = CrossRewriteProduct[];

export type ProductReview = {
  productReviewId: string;
  productId: string;
  locale: I18nLocale;
  isActive: boolean;
  name: string;
  title: string;
  review: string;
  answer: string;
  ratingDescription: number;
  ratingPrice: number;
  ratingQuality: number;
  createdAt: string;
};

export type ProductReviewsResponse = ProductReview[];

export type ProductDescription = {
  productId: string;
  locale: string;
  isVerified: boolean;
  gallery: string[];
  rows: ProductDescriptionRow[];
};

export type ProductDescriptionRow =
  | ProductDescriptionTextRow
  | ProductDescriptionImageRow
  | ProductDescriptionVideoRow
  | ProductDescriptionTextImageRow
  | ProductDescriptionTextVideoRow
  | ProductDescriptionImageVideoRow;

export enum ProductDescriptionRowType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  TEXT_TEXT = 'text-text',
  IMAGE_IMAGE = 'image-image',
  VIDEO_VIDEO = 'video-video',
  IMAGE_TEXT = 'image-text',
  TEXT_IMAGE = 'text-image',
  VIDEO_TEXT = 'video-text',
  TEXT_VIDEO = 'text-video',
  IMAGE_VIDEO = 'image-video',
  VIDEO_IMAGE = 'video-image',
  LIBRARY = 'library',
}

export type ProductDescriptionTextRow = {
  type: ProductDescriptionRowType.TEXT | ProductDescriptionRowType.TEXT_TEXT;
  columns: ProductDescriptionTextColumn[];
};

export type ProductDescriptionImageRow = {
  type: ProductDescriptionRowType.IMAGE | ProductDescriptionRowType.IMAGE_IMAGE;
  columns: ProductDescriptionImageColumn[];
};

export type ProductDescriptionVideoRow = {
  type: ProductDescriptionRowType.VIDEO | ProductDescriptionRowType.VIDEO_VIDEO;
  columns: ProductDescriptionVideoColumn[];
};

export type ProductDescriptionTextImageRow = {
  type:
    | ProductDescriptionRowType.TEXT_IMAGE
    | ProductDescriptionRowType.IMAGE_TEXT;
  columns: (ProductDescriptionImageColumn | ProductDescriptionTextColumn)[];
};

export type ProductDescriptionTextVideoRow = {
  type:
    | ProductDescriptionRowType.TEXT_VIDEO
    | ProductDescriptionRowType.VIDEO_TEXT;
  columns: (ProductDescriptionVideoColumn | ProductDescriptionTextColumn)[];
};

export type ProductDescriptionImageVideoRow = {
  type:
    | ProductDescriptionRowType.IMAGE_VIDEO
    | ProductDescriptionRowType.VIDEO_IMAGE;
  columns: (ProductDescriptionVideoColumn | ProductDescriptionImageColumn)[];
};

export enum ProductDescriptionColumnType {
  TEXT_CONTENT = 'text-content',
  VIDEO_CONTENT = 'video-content',
  IMAGE_CONTENT = 'image-content',
}

export type ProductDescriptionColumns =
  | ProductDescriptionTextColumn
  | ProductDescriptionVideoColumn
  | ProductDescriptionImageColumn;

export type ProductDescriptionTextColumn = {
  type: ProductDescriptionColumnType.TEXT_CONTENT;
  content: {
    text: {
      text: string;
    };
  };
};

export type ProductDescriptionVideoColumn = {
  type: ProductDescriptionColumnType.VIDEO_CONTENT;
  content: {
    video: {
      url: string;
    };
  };
};

export type ProductDescriptionImageColumn = {
  type: ProductDescriptionColumnType.IMAGE_CONTENT;
  content: {
    image: {
      url: string;
      alt: string;
      hyperlink: string;
      hyperlinkTitle: string;
      hyperlinkTarget: string;
    };
  };
};
